import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Activity, Eye, Calendar, Clock, User, ChevronLeft, 
  ChevronRight, Filter, Search, Building2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import HelpIcon from '@/components/HelpIcon';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Activity type colors
const ACTIVITY_COLORS = {
  project_created: '#22c55e',
  project_updated: '#3b82f6',
  project_phase_changed: '#8b5cf6',
  task_created: '#f59e0b',
  task_completed: '#22c55e',
  task_assigned: '#06b6d4',
  document_added: '#6366f1',
  document_signed: '#22c55e',
  message_sent: '#ec4899',
  message_received: '#ec4899',
  note_added: '#f97316',
  clip_added: '#14b8a6',
  expense_logged: '#ef4444',
  memo_added: '#a855f7',
  time_logged: '#0ea5e9',
  custom: '#6b7280',
};

// Activity type icons
const getActivityIcon = (type) => {
  const icons = {
    project_created: '🎉',
    task_completed: '✓',
    task_created: '📋',
    document_added: '📄',
    document_signed: '✍️',
    message_sent: '💬',
    note_added: '📝',
    clip_added: '📎',
    expense_logged: '💰',
    memo_added: '🗒️',
    time_logged: '⏱️',
    project_updated: '🔄',
    project_phase_changed: '📊',
  };
  return icons[type] || '•';
};

// Get day of week abbreviation
const getDayAbbr = (dateStr) => {
  const days = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  const date = new Date(dateStr + 'T00:00:00');
  return days[date.getDay()];
};

export default function AllActivityPage() {
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('keyMadeDarkMode') === 'true');
  const [projects, setProjects] = useState([]);
  const [allActivity, setAllActivity] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Listen for dark mode changes
  useEffect(() => {
    const interval = setInterval(() => {
      const saved = localStorage.getItem('keyMadeDarkMode');
      setDarkMode(saved === 'true');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Fetch all projects
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/projects`);
        const data = response.data;
        setProjects(data.projects || data || []);
      } catch (error) {
        console.error('Error fetching projects:', error);
      }
    };
    fetchProjects();
  }, []);

  // Fetch activity for all projects
  const fetchAllActivity = useCallback(async () => {
    setLoading(true);
    try {
      const projectsToFetch = selectedProject === 'all' 
        ? projects 
        : projects.filter(p => p.id === selectedProject);
      
      if (projectsToFetch.length === 0) {
        setAllActivity({});
        setLoading(false);
        return;
      }

      // Fetch activity for each project
      const allData = {};
      const endDate = new Date();
      const startDate = new Date();
      startDate.setFullYear(startDate.getFullYear() - 1);

      await Promise.all(projectsToFetch.map(async (project) => {
        try {
          const response = await axios.get(
            `${BACKEND_URL}/api/projects/${project.id}/activity-calendar`,
            { params: { start_date: startDate.toISOString(), end_date: endDate.toISOString() } }
          );
          const data = response.data;
          // Merge calendar data
          Object.entries(data.calendar || {}).forEach(([dateStr, dayData]) => {
              if (!allData[dateStr]) {
                allData[dateStr] = { activities: [], views: [] };
              }
              // Add project info to each activity
              dayData.activities?.forEach(activity => {
                allData[dateStr].activities.push({
                  ...activity,
                  project_id: project.id,
                  project_name: project.name
                });
              });
              dayData.views?.forEach(view => {
                allData[dateStr].views.push({
                  ...view,
                  project_id: project.id,
                  project_name: project.name
                });
              });
            });
        } catch (error) {
          console.error(`Error fetching activity for project ${project.id}:`, error);
        }
      }));

      setAllActivity(allData);
    } catch (error) {
      console.error('Error fetching all activity:', error);
    } finally {
      setLoading(false);
    }
  }, [projects, selectedProject]);

  useEffect(() => {
    if (projects.length > 0) {
      fetchAllActivity();
    }
  }, [projects, fetchAllActivity]);

  // Group activity by month (only months with activity)
  const getMonthsWithActivity = () => {
    const months = {};
    
    Object.entries(allActivity).forEach(([dateStr, data]) => {
      const hasContent = data.activities?.length > 0 || data.views?.length > 0;
      if (!hasContent) return;
      
      // Filter by search query
      if (searchQuery) {
        const matchingActivities = data.activities?.filter(a => 
          a.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          a.project_name?.toLowerCase().includes(searchQuery.toLowerCase())
        );
        const matchingViews = data.views?.filter(v => 
          v.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          v.project_name?.toLowerCase().includes(searchQuery.toLowerCase())
        );
        if (matchingActivities?.length === 0 && matchingViews?.length === 0) return;
      }
      
      const date = new Date(dateStr + 'T00:00:00');
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!months[monthKey]) {
        months[monthKey] = {
          year: date.getFullYear(),
          month: date.getMonth(),
          monthName: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
          days: {}
        };
      }
      
      months[monthKey].days[dateStr] = data;
    });
    
    // Sort months in reverse chronological order
    return Object.entries(months)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([key, value]) => ({ key, ...value }));
  };

  // Generate mini calendar for a month
  const generateMiniCalendar = (year, month, daysWithActivity) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = firstDay.getDay();
    
    const days = [];
    
    for (let i = 0; i < startPadding; i++) {
      days.push({ date: null });
    }
    
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const hasActivity = daysWithActivity[dateStr];
      days.push({
        date: dateStr,
        day,
        hasActivity: !!hasActivity,
        activityCount: hasActivity?.activities?.length || 0,
        viewCount: hasActivity?.views?.length || 0
      });
    }
    
    return days;
  };

  const monthsWithActivity = getMonthsWithActivity();
  
  // Stats
  const totalDays = Object.keys(allActivity).length;
  const totalActivities = Object.values(allActivity).reduce((sum, day) => sum + (day.activities?.length || 0), 0);
  const totalViews = Object.values(allActivity).reduce((sum, day) => sum + (day.views?.length || 0), 0);

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-black' : 'bg-[#F8F7F5]'}`}>
      {/* Header */}
      <header className={`${darkMode ? 'bg-[#0D0D0D] border-[#1A1A1A]' : 'bg-white border-border'} border-b px-6 py-4`}>
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/')}
                className={darkMode ? 'text-[#8A8A8A] hover:text-[#E5E2DD]' : ''}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to My Desk
              </Button>
              <div className={`h-6 w-px ${darkMode ? 'bg-[#2A2A2A]' : 'bg-border'}`} />
              <div className="flex items-center gap-2">
                <HelpIcon elementId="all-activity" position="inline" size="md" />
                <Activity className="w-5 h-5 text-[#BC5D41]" />
                <h1 className={`text-xl font-heading ${darkMode ? 'text-[#E5E2DD]' : ''}`}>
                  All Activity
                </h1>
              </div>
            </div>
            
            {/* Stats */}
            <div className="flex items-center gap-4">
              <Badge variant="secondary" className="text-xs">
                {totalDays} active days
              </Badge>
              <Badge variant="secondary" className="text-xs">
                {totalActivities} activities
              </Badge>
              <Badge variant="secondary" className="text-xs">
                {totalViews} views
              </Badge>
            </div>
          </div>
          
          {/* Filters */}
          <div className="flex items-center gap-4 mt-4">
            <div className="flex-1 max-w-xs">
              <div className="relative">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${darkMode ? 'text-[#6B6B6B]' : 'text-muted-foreground'}`} />
                <Input
                  placeholder="Search activities..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`pl-9 ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A] text-[#E5E2DD]' : ''}`}
                />
              </div>
            </div>
            
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger className={`w-[200px] ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A] text-[#E5E2DD]' : ''}`}>
                <SelectValue placeholder="Filter by project" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {projects.map(project => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#BC5D41]" />
          </div>
        ) : monthsWithActivity.length === 0 ? (
          <Card className={`${darkMode ? 'bg-[#0D0D0D] border-[#1A1A1A]' : ''}`}>
            <CardContent className="p-12 text-center">
              <Calendar className={`w-12 h-12 mx-auto mb-4 ${darkMode ? 'text-[#6B6B6B]' : 'text-muted-foreground'}`} />
              <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-[#E5E2DD]' : ''}`}>
                No Activity Found
              </h3>
              <p className={`text-sm ${darkMode ? 'text-[#6B6B6B]' : 'text-muted-foreground'}`}>
                {searchQuery 
                  ? 'No activities match your search. Try a different query.'
                  : 'Start working on projects to see activity here.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Timeline */}
            <div className="lg:col-span-2 space-y-6">
              {monthsWithActivity.map((monthData) => {
                const miniCalDays = generateMiniCalendar(monthData.year, monthData.month, monthData.days);
                const sortedDays = Object.entries(monthData.days)
                  .sort(([a], [b]) => b.localeCompare(a));
                
                return (
                  <Card key={monthData.key} className={`${darkMode ? 'bg-[#0D0D0D] border-[#1A1A1A]' : ''}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className={`text-base font-medium ${darkMode ? 'text-[#BC5D41]' : 'text-[#70170F]'}`}>
                          {monthData.monthName}
                        </CardTitle>
                        <Badge variant="secondary" className="text-[10px]">
                          {Object.keys(monthData.days).length} days
                        </Badge>
                      </div>
                      
                      {/* Mini Calendar */}
                      <div className="mt-3 grid grid-cols-7 gap-1 text-center">
                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                          <div key={i} className={`text-[9px] ${darkMode ? 'text-[#6B6B6B]' : 'text-muted-foreground'}`}>
                            {d}
                          </div>
                        ))}
                        {miniCalDays.map((dayInfo, idx) => (
                          <div key={idx} className="aspect-square flex items-center justify-center">
                            {dayInfo.date ? (
                              <div
                                className={`w-full h-full text-[10px] rounded-sm flex items-center justify-center relative ${
                                  dayInfo.hasActivity 
                                    ? darkMode 
                                      ? 'bg-[#BC5D41]/30 text-[#E5E2DD]' 
                                      : 'bg-[#BC5D41]/20'
                                    : darkMode 
                                      ? 'text-[#6B6B6B]' 
                                      : 'text-muted-foreground'
                                }`}
                              >
                                {dayInfo.day}
                              </div>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </CardHeader>
                    
                    <CardContent className="pt-0">
                      <div className={`border-l-2 ml-2 pl-3 space-y-2 ${darkMode ? 'border-[#2A2A2A]' : 'border-border'}`}>
                        {sortedDays.map(([dateStr, dayData]) => {
                          const date = new Date(dateStr + 'T00:00:00');
                          const dayNum = date.getDate();
                          const dayAbbr = getDayAbbr(dateStr);
                          
                          return (
                            <div key={dateStr} className="space-y-1">
                              {/* Date Header */}
                              <div className={`flex items-center gap-2 ${darkMode ? 'text-[#8A8A8A]' : 'text-muted-foreground'}`}>
                                <span className="text-sm font-medium">{dayNum}</span>
                                <span className="text-xs">{dayAbbr}</span>
                              </div>
                              
                              {/* Activities */}
                              {dayData.activities?.map((activity, idx) => (
                                <div 
                                  key={`a-${idx}`}
                                  className={`flex items-start gap-2 p-2 rounded-lg border-l-2 ${darkMode ? 'bg-[#1A1A1A]' : 'bg-muted/30'}`}
                                  style={{ borderLeftColor: ACTIVITY_COLORS[activity.activity_type] || '#6b7280' }}
                                >
                                  <span className="text-sm">{getActivityIcon(activity.activity_type)}</span>
                                  <div className="flex-1 min-w-0">
                                    <p className={`text-sm font-medium ${darkMode ? 'text-[#E5E2DD]' : ''}`}>
                                      {activity.title}
                                    </p>
                                    {activity.project_name && (
                                      <button
                                        onClick={() => navigate(`/projects/${activity.project_id}/hub`)}
                                        className="flex items-center gap-1 text-xs text-[#BC5D41] hover:underline"
                                      >
                                        <Building2 className="w-3 h-3" />
                                        {activity.project_name}
                                      </button>
                                    )}
                                    <div className={`flex items-center gap-2 mt-1 text-[10px] ${darkMode ? 'text-[#6B6B6B]' : 'text-muted-foreground'}`}>
                                      <Clock className="w-2.5 h-2.5" />
                                      {new Date(activity.created_at).toLocaleTimeString('en-US', { 
                                        hour: 'numeric', 
                                        minute: '2-digit' 
                                      })}
                                      {activity.actor_name && (
                                        <>
                                          <span>•</span>
                                          <User className="w-2.5 h-2.5" />
                                          <span>{activity.actor_name}</span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                              
                              {/* Views */}
                              {dayData.views?.length > 0 && (
                                <div className={`flex items-center gap-2 p-2 rounded text-xs ${darkMode ? 'bg-[#1A1A1A]/50' : 'bg-gray-50'}`}>
                                  <Eye className="w-3 h-3 text-gray-400" />
                                  <span className={darkMode ? 'text-[#8A8A8A]' : 'text-muted-foreground'}>
                                    {dayData.views.length} view{dayData.views.length > 1 ? 's' : ''}
                                  </span>
                                  {dayData.views.slice(0, 3).map((view, idx) => (
                                    <TooltipProvider key={idx}>
                                      <Tooltip>
                                        <TooltipTrigger>
                                          <Badge variant="outline" className="text-[9px]">
                                            {view.user_name}
                                          </Badge>
                                        </TooltipTrigger>
                                        <TooltipContent side="top" className="text-xs">
                                          <p>{view.project_name}</p>
                                          <p>{view.times?.length || 1} view(s)</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            
            {/* Right Column - Summary */}
            <div className="space-y-4">
              <Card className={`${darkMode ? 'bg-[#0D0D0D] border-[#1A1A1A]' : ''} sticky top-6`}>
                <CardHeader>
                  <CardTitle className={`text-sm ${darkMode ? 'text-[#E5E2DD]' : ''}`}>
                    Activity Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className={darkMode ? 'text-[#8A8A8A]' : 'text-muted-foreground'}>
                        Total Activities
                      </span>
                      <span className={`font-medium ${darkMode ? 'text-[#E5E2DD]' : ''}`}>
                        {totalActivities}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className={darkMode ? 'text-[#8A8A8A]' : 'text-muted-foreground'}>
                        Total Views
                      </span>
                      <span className={`font-medium ${darkMode ? 'text-[#E5E2DD]' : ''}`}>
                        {totalViews}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className={darkMode ? 'text-[#8A8A8A]' : 'text-muted-foreground'}>
                        Active Days
                      </span>
                      <span className={`font-medium ${darkMode ? 'text-[#E5E2DD]' : ''}`}>
                        {totalDays}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className={darkMode ? 'text-[#8A8A8A]' : 'text-muted-foreground'}>
                        Projects Tracked
                      </span>
                      <span className={`font-medium ${darkMode ? 'text-[#E5E2DD]' : ''}`}>
                        {projects.length}
                      </span>
                    </div>
                  </div>
                  
                  {/* Legend */}
                  <div className={`pt-4 border-t ${darkMode ? 'border-[#1A1A1A]' : 'border-border'}`}>
                    <p className={`text-xs font-medium mb-2 ${darkMode ? 'text-[#8A8A8A]' : 'text-muted-foreground'}`}>
                      Activity Types
                    </p>
                    <div className="space-y-1">
                      {[
                        { type: 'task_completed', label: 'Task Completed' },
                        { type: 'document_added', label: 'Document Added' },
                        { type: 'note_added', label: 'Note Added' },
                        { type: 'project_phase_changed', label: 'Phase Changed' },
                        { type: 'message_sent', label: 'Message' },
                      ].map(item => (
                        <div key={item.type} className="flex items-center gap-2 text-xs">
                          <div 
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: ACTIVITY_COLORS[item.type] }}
                          />
                          <span className={darkMode ? 'text-[#8A8A8A]' : 'text-muted-foreground'}>
                            {item.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
